import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import type { WhatsAppAccount, WhatsAppSetupConfig, UseWhatsAppAccountResult } from '@/types/whatsapp';

export function useWhatsAppAccount(businessId: string | null): UseWhatsAppAccountResult {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);

  const queryKey = ['whatsapp-account', businessId];

  const { data: account, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return null;

      const { data, error } = await supabase
        .from('whatsapp_accounts')
        .select('*')
        .eq('business_id', businessId)
        .maybeSingle();

      if (error) throw error;
      return data as WhatsAppAccount | null;
    },
    enabled: !!businessId,
  });

  const connectMutation = useMutation({
    mutationFn: async (config: WhatsAppSetupConfig) => {
      if (!businessId) throw new Error('No business selected');

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('whatsapp-setup', {
        body: {
          action: 'connect',
          business_id: businessId,
          waba_id: config.waba_id,
          phone_number_id: config.phone_number_id,
          access_token: config.access_token,
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('WhatsApp connected', {
        description: `Connected ${data.account.display_name || data.account.phone_number}`,
      });
    },
    onError: (error: Error) => {
      toast.error('Connection failed', {
        description: error.message,
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('No business selected');

      const response = await supabase.functions.invoke('whatsapp-setup', {
        body: {
          action: 'disconnect',
          business_id: businessId,
        },
      });

      if (response.error) throw response.error;
      if (response.data?.error) throw new Error(response.data.error);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('WhatsApp disconnected');
    },
    onError: (error: Error) => {
      toast.error('Disconnection failed', {
        description: error.message,
      });
    },
  });

  const connect = useCallback(async (config: WhatsAppSetupConfig) => {
    setIsConnecting(true);
    try {
      await connectMutation.mutateAsync(config);
    } finally {
      setIsConnecting(false);
    }
  }, [connectMutation]);

  const disconnect = useCallback(async () => {
    await disconnectMutation.mutateAsync();
  }, [disconnectMutation]);

  return {
    account,
    isLoading: isLoading || isConnecting,
    error: error as Error | null,
    isConnected: !!account && account.status === 'active',
    connect,
    disconnect,
    refresh: refetch,
  };
}
